import { memo, useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { htmlEncode } from 'js-htmlencode';
import { chartGlobalOptions, createChartsTooltip, lineChartOptions, rotatedXAxisLabelsOptions } from '../../chart';
import { baseDarkColour, rainbowPalette } from '../../palette';
import { currencyFormat, decimalFormat, percentFormat, translate } from '../../utils';

const FORECAST_METRICS = [
    { metricKey: 'occupancy', labelTranslationKey: 'PORTFOLIO_DASHBOARD_OCCUPANCY', valueFormat: 'percent' },
    { metricKey: 'revenue', labelTranslationKey: 'PORTFOLIO_DASHBOARD_REVENUE', valueFormat: 'currency' },
    { metricKey: 'adr', labelTranslationKey: 'PORTFOLIO_DASHBOARD_ADR', valueFormat: 'currency' },
    { metricKey: 'revPAR', labelTranslationKey: 'PORTFOLIO_DASHBOARD_REVPAR', valueFormat: 'currency' },
    { metricKey: 'occupiedRooms', labelTranslationKey: 'PORTFOLIO_DASHBOARD_OCCUPIED_ROOMS', valueFormat: 'integer' },
];

const PAST_FORECAST_STROKE_DASH_ARRAY = 4;

// ApexCharts adds labels.offsetX twice when placing a category label, and the x-annotation copies that label x.
const TODAY_LINE_OFFSET_X = -2 * rotatedXAxisLabelsOptions.labels.offsetX;

/** @param {{ forecast: import('./portfolioDashboard.types').PortfolioForecast }} props */
export const PortfolioForecast = memo(function PortfolioForecast({ forecast }) {
    const [metricKey, setMetricKey] = useState('occupancy');
    const chartContainerRef = useRef(null);

    useEffect(() => {
        const chartElement = chartContainerRef.current;
        const categories = forecast.dates.map((date) => moment(date).format('MMM Do'));
        const todayIndex = forecast.dates.indexOf(forecast.today);
        const propertyColors = rainbowPalette(forecast.properties.length);
        const selectedForecastMetric = FORECAST_METRICS.find((forecastMetric) => forecastMetric.metricKey === metricKey);
        const isPercent = selectedForecastMetric.valueFormat === 'percent';
        const isInteger = selectedForecastMetric.valueFormat === 'integer';
        const series = buildForecastChartSeries(forecast.properties, metricKey, isPercent, todayIndex);
        const seriesColors = propertyColors.flatMap((propertyColor) => [propertyColor, propertyColor]);

        const chart = new ApexCharts(chartElement, {
            ...chartGlobalOptions,
            chart: {
                ...chartGlobalOptions.chart,
                type: 'line',
                height: 360,
                width: '100%',
                events: {
                    mounted: (mountedChart) => {
                        hidePastForecastLegendItems(mountedChart);
                        mountedChart.el.addEventListener('mousemove', (event) => {
                            revealPairedForecastSeries(mountedChart, event);
                        });
                    },
                    updated: hidePastForecastLegendItems,
                    legendClick: togglePairedForecastSeries,
                },
            },
            series,
            colors: seriesColors,
            legend: {
                ...chartGlobalOptions.legend,
                // ApexCharts writes legend text with innerHTML.
                formatter: (seriesName) => htmlEncode(seriesName),
            },
            xaxis: {
                ...rotatedXAxisLabelsOptions,
                categories,
                tickAmount: 20,
            },
            yaxis: isPercent
                ? {
                    min: 0,
                    max: 100,
                    labels: {
                        formatter: (value) => `${value}%`,
                    },
                }
                : {
                    min: 0,
                    labels: {
                        formatter: (value) => (isInteger ? decimalFormat(value, 0) : currencyFormat(value)),
                    },
                },
            annotations: {
                xaxis: [{
                    x: categories[todayIndex],
                    borderColor: baseDarkColour,
                    strokeDashArray: 0,
                    offsetX: TODAY_LINE_OFFSET_X,
                    label: {
                        text: translate('frontend', 'PORTFOLIO_DASHBOARD_FORECAST_TODAY'),
                        orientation: 'horizontal',
                        offsetX: TODAY_LINE_OFFSET_X,
                        borderColor: baseDarkColour,
                        style: {
                            color: baseDarkColour,
                        },
                    },
                }],
            },
            tooltip: {
                ...chartGlobalOptions.tooltip,
                custom: ({ dataPointIndex, w }) => createChartsTooltip(
                    categories[dataPointIndex],
                    forecast.properties.flatMap((propertyForecast, propertyIndex) => {
                        const futureSeriesIndex = propertyIndex * 2 + 1;
                        if (w.globals.collapsedSeriesIndices.includes(futureSeriesIndex)) return [];

                        const metricValue = propertyForecast[metricKey][dataPointIndex];

                        return [{
                            name: propertyForecast.name,
                            value: isPercent
                                ? percentFormat(metricValue, 1)
                                : isInteger
                                    ? decimalFormat(metricValue, 0)
                                    : currencyFormat(metricValue),
                            color: propertyColors[propertyIndex],
                        }];
                    }),
                ),
            },
            markers: {
                ...lineChartOptions.markers,
                size: 0,
            },
            stroke: {
                ...lineChartOptions.stroke,
                dashArray: forecast.properties.flatMap(() => [PAST_FORECAST_STROKE_DASH_ARRAY, 0]),
            },
        });

        chart.render();

        return () => {
            chart.destroy();
        };
    }, [forecast.dates, forecast.today, forecast.properties, metricKey]);

    const handleMetricClick = (event) => {
        event.preventDefault();

        const selectedMetricKey = event.currentTarget.dataset.metricKey;
        if (selectedMetricKey === metricKey) return;

        setMetricKey(selectedMetricKey);
    };

    return (
        <div className="box box-solid mb-0">
            <div className="box-header">
                <h3 className="box-title">
                    {translate('frontend', 'PORTFOLIO_DASHBOARD_FORECAST')}
                </h3>

                <div className="box-tools">
                    <ul className="nav nav-pills">
                        {FORECAST_METRICS.map((forecastMetric) => (
                            <li
                                key={forecastMetric.metricKey}
                                className={metricKey === forecastMetric.metricKey ? 'active' : undefined}
                            >
                                <a href="#" data-metric-key={forecastMetric.metricKey} onClick={handleMetricClick}>
                                    {translate('frontend', forecastMetric.labelTranslationKey)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="box-body">
                <div ref={chartContainerRef}></div>
            </div>
        </div>
    );
});

/**
 * @param {import('./portfolioDashboard.types').PortfolioForecastProperty[]} properties
 * @param {string} metricKey
 * @param {boolean} isPercent
 * @param {number} todayIndex
 * @returns {{ name: string, data: (number | null)[] }[]}
 */
function buildForecastChartSeries(properties, metricKey, isPercent, todayIndex) {
    // ApexCharts dashes a whole series, so each property is a dashed series through today and a solid series from today.
    return properties.flatMap((propertyForecast) => {
        const metricValues = isPercent
            ? propertyForecast[metricKey].map((value) => value * 100)
            : propertyForecast[metricKey];

        return [
            {
                name: propertyForecast.name,
                data: metricValues.map((value, pointIndex) => (pointIndex <= todayIndex ? value : null)),
            },
            {
                name: propertyForecast.name,
                data: metricValues.map((value, pointIndex) => (pointIndex >= todayIndex ? value : null)),
            },
        ];
    });
}

/**
 * @param {ApexCharts} chart
 */
function hidePastForecastLegendItems(chart) {
    const legendItems = chart.el.querySelectorAll('.apexcharts-legend-series');

    legendItems.forEach((legendItem) => {
        const seriesIndex = Number(legendItem.getAttribute('rel')) - 1;
        if (seriesIndex % 2 !== 0) return;

        legendItem.style.display = 'none';
    });
}

/**
 * @param {ApexCharts} chart
 * @param {MouseEvent} event
 */
function revealPairedForecastSeries(chart, event) {
    const legendItem = /** @type {Element} */ (event.target).closest('.apexcharts-legend-series');
    if (!legendItem) return;

    const seriesIndex = Number(legendItem.getAttribute('rel')) - 1;
    // Highlight matches the first series with this name, which is the dashed half.
    [seriesIndex, pairedForecastSeriesIndex(seriesIndex)].forEach((forecastSeriesIndex) => {
        const seriesElement = chart.el.querySelector(
            `.apexcharts-series[data\\:realIndex='${forecastSeriesIndex}']`,
        );
        seriesElement.classList.remove('legend-mouseover-inactive');
    });
}

/**
 * @param {ApexCharts} chart
 * @param {number} seriesIndex
 */
function togglePairedForecastSeries(chart, seriesIndex) {
    const pairedSeriesIndex = pairedForecastSeriesIndex(seriesIndex);
    const isHidden = chart.w.globals.collapsedSeriesIndices.includes(seriesIndex);

    // ApexCharts toggles the clicked series after this handler. Change the other half first so one update changes both.
    if (isHidden) {
        restoreCollapsedForecastSeries(chart, pairedSeriesIndex);
        return;
    }

    chart.legend.legendHelpers.getSeriesAfterCollapsing({ realIndex: pairedSeriesIndex });
}

/**
 * @param {ApexCharts} chart
 * @param {number} seriesIndex
 */
function restoreCollapsedForecastSeries(chart, seriesIndex) {
    const collapsedSeriesIndices = chart.w.globals.collapsedSeriesIndices;
    const collapsedPosition = collapsedSeriesIndices.indexOf(seriesIndex);
    const collapsedSeries = chart.w.globals.collapsedSeries[collapsedPosition];

    chart.w.config.series[seriesIndex].data = collapsedSeries.data.slice();
    chart.w.config.series[seriesIndex].hidden = false;
    chart.w.globals.collapsedSeries.splice(collapsedPosition, 1);
    collapsedSeriesIndices.splice(collapsedPosition, 1);
    chart.w.globals.risingSeries.push(seriesIndex);
}

/**
 * @param {number} seriesIndex
 * @returns {number}
 */
function pairedForecastSeriesIndex(seriesIndex) {
    return seriesIndex % 2 === 0 ? seriesIndex + 1 : seriesIndex - 1;
}
